import { create } from "zustand";
import JobService from "services/JobService";

interface DraftItem {
  draft_id: string;
  last_saved_at?: string;
  draft_type?: string;
  [key: string]: any;
}

interface DraftJobData {
  draft_id: string;
  job_title?: string;
  no_of_openings?: string;
  category_id?: number;
  job_city_id?: string;
  job_locality_id?: string;
  gender_req?: string;
  minimum_qualification?: string;
  is_fresher_allowed?: boolean;
  minSal?: string;
  maxSal?: string;
  is_incentive?: boolean;
  job_skills?: Array<{
    option_id: number | string;
    label?: string;
  }>;
  job_type?: string;
  working_days?: {
    option_id: number | string;
    label?: string;
  };
  job_perks?: Array<{
    option_id: number | string;
    label?: string;
  }>;
  shift_type?: {
    option_id: number | string;
    label?: string;
  };
  call_allowed?: boolean;
  job_contact?: string;
  enhanced_job_description?: string;
  assets?: any[];
  interview_address?: string;
  is_wfh?: boolean;
  is_contract?: boolean;
  is_additional_fee_applicable?: boolean;
  prior_experience_type?: {
    option_id: number | string;
    label?: string;
  };
  client_name?: string;
  client_address?: string;
  job_type_location?: string;
  incentives?: number;
  lastSavedAt?: string;
  [key: string]: any;
}

interface DraftJobState {
  // State
  isLoading: boolean;
  error: string | null;
  currentDraftData: DraftJobData | null;

  // Actions
  fetchDraftData: (draftId: string) => Promise<DraftJobData | null>;
  clearDraftData: () => void;
  setError: (error: string | null) => void;
  showDraftResetBanner: boolean;
  setShowDraftResetBanner: (visible: boolean) => void;
}

export const useDraftJobStore = create<DraftJobState>((set, get) => ({
  // Initial state
  isLoading: false,
  error: null,
  currentDraftData: null,
  showDraftResetBanner: false,
  // Actions
  fetchDraftData: async (draftId: string): Promise<DraftJobData | null> => {
    if (!draftId) {
      const error = "Draft ID is required";
      set({ error });
      throw new Error(error);
    }

    set({ isLoading: true, error: null });

    try {
      const response = await JobService.getDraftData(draftId);

      if (response?.status && response?.data) {
        const draftData = response.data as DraftJobData;
        set({
          currentDraftData: draftData,
          isLoading: false,
          error: null
        });
        return draftData;
      } else {
        const error = "Invalid draft response format";
        set({
          isLoading: false,
          error,
          currentDraftData: null
        });
        throw new Error(error);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch draft data";
      set({
        isLoading: false,
        error: errorMessage,
        currentDraftData: null
      });
      console.error("Error fetching draft data:", error);
      throw error;
    }
  },

  clearDraftData: () => {
    set({
      currentDraftData: null,
      error: null,
      isLoading: false
    });
  },

  setError: (error: string | null) => {
    set({ error });
  },
  setShowDraftResetBanner: (visible: boolean) => set({ showDraftResetBanner: visible })
}));

export type { DraftItem, DraftJobData };
